import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "../ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const reportId = searchParams.get("report") || "";
  
  const [status, setStatus] = useState("pending"); // pending, loading, success, error
  const [inputEmail, setInputEmail] = useState(email);

  const handleUnsubscribe = async () => {
    if (!inputEmail) {
      alert("Please enter your email address");
      return;
    }
    
    setStatus("loading");
    
    try {
      const response = await fetch(`${API}/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email: inputEmail,
          report_id: reportId 
        }),
      });
      
      if (response.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <span className="text-xl font-bold text-white">
              Loca<span className="text-emerald-400">Leap</span>
            </span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-16">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {status === "success" ? (
          <div className="text-center">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">You've Been Unsubscribed</h1>
            <p className="text-slate-400 mb-8">
              You will no longer receive follow-up emails from us. We're sorry to see you go!
            </p>
            <p className="text-slate-500 text-sm">
              Changed your mind? You can always generate a new report at{" "}
              <Link to="/create" className="text-emerald-400 hover:underline">LocaLeap</Link>
            </p>
          </div>
        ) : status === "error" ? (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">Something Went Wrong</h1>
            <p className="text-slate-400 mb-8">
              We couldn't process your unsubscribe request. Please try again or contact us directly.
            </p>
            <Button
              onClick={() => setStatus("pending")}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              Try Again
            </Button>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3 mb-8">
              <Mail className="w-10 h-10 text-emerald-400" />
              <h1 className="text-3xl font-bold text-white">Unsubscribe</h1>
            </div>

            <p className="text-slate-400 mb-8">
              We're sorry to see you go. Enter your email below to unsubscribe from our follow-up emails.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inputEmail}
                  onChange={(e) => setInputEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full h-12 px-4 bg-slate-800/50 border border-slate-700 text-white placeholder:text-slate-500 rounded-xl focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>

              <Button
                onClick={handleUnsubscribe}
                disabled={status === "loading"}
                className="w-full bg-red-600 hover:bg-red-700 py-6 rounded-xl"
              >
                {status === "loading" ? "Processing..." : "Unsubscribe from Emails"}
              </Button>

              <p className="text-slate-500 text-sm text-center">
                You will stop receiving follow-up emails about your brand strategy report.
                You can still access your report anytime.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Simple Footer */}
      <div className="border-t border-slate-800 py-8 px-4 mt-auto">
        <div className="max-w-4xl mx-auto text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} LocaLeap by VSW Agency / Merge Publishing. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
